// src/components/chat/ArtifactPreviewPanel.tsx
// Slide-in panel for previewing generated artifacts (PDF, XLSX, HTML, etc.)

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import {
  X,
  Download,
  ChevronLeft,
  ChevronRight,
  FileText,
  FileSpreadsheet,
  FileType2,
  Presentation,
  Loader2,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { v2ChatApi } from '@/services/v2-chat-api';
import { ensureFreshAccessToken } from '@/services/session';
import type { ArtifactInfo } from '@/types/chat';

interface ArtifactPreviewPanelProps {
  artifact: ArtifactInfo;
  isOpen: boolean;
  onClose: () => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** File types the browser can render natively via iframe */
const IFRAME_TYPES = new Set(['pdf', 'html', 'txt']);

/** Get icon + color based on file type */
function getFileTypeIcon(fileType: string): { icon: React.ElementType; bgClass: string; iconClass: string } {
  switch (fileType) {
    case 'xlsx':
    case 'csv':
      return { icon: FileSpreadsheet, bgClass: 'bg-green-100 dark:bg-green-900/40', iconClass: 'text-green-600 dark:text-green-400' };
    case 'docx':
      return { icon: FileType2, bgClass: 'bg-blue-100 dark:bg-blue-900/40', iconClass: 'text-blue-600 dark:text-blue-400' };
    case 'pptx':
      return { icon: Presentation, bgClass: 'bg-amber-100 dark:bg-amber-900/40', iconClass: 'text-amber-600 dark:text-amber-400' };
    case 'md':
      return { icon: FileText, bgClass: 'bg-accent', iconClass: 'text-primary' };
    case 'txt':
      return { icon: FileText, bgClass: 'bg-muted', iconClass: 'text-muted-foreground' };
    default:
      return { icon: FileText, bgClass: 'bg-red-100 dark:bg-red-900/40', iconClass: 'text-red-600 dark:text-red-400' };
  }
}

// ─── XLSX Table Preview ──────────────────────────────────────────────

interface XlsxPreviewData {
  id: string;
  fileType: string;
  title: string;
  filename: string;
  content: string;
  metadata: Record<string, unknown>;
}

interface XlsxSheet {
  name: string;
  columns: { header: string; key: string; format?: string }[];
  rows: Record<string, unknown>[];
}

function formatCellValue(value: unknown, format?: string): string {
  if (value === null || value === undefined) return '';
  if (format === 'currency' && typeof value === 'number') {
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }
  if (format === 'percent' && typeof value === 'number') {
    return (value * 100).toFixed(2) + '%';
  }
  if (format === 'number' && typeof value === 'number') {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return String(value);
}

const XlsxPreview: React.FC<{ artifactId: string }> = ({ artifactId }) => {
  const [activeSheet, setActiveSheet] = useState(0);

  const { data, isLoading, error } = useQuery({
    queryKey: ['artifact-preview-xlsx', artifactId],
    queryFn: async (): Promise<XlsxPreviewData> => {
      await ensureFreshAccessToken();
      const token = v2ChatApi.getAuthToken();
      const url = v2ChatApi.getArtifactPreviewUrl(artifactId);
      const res = await fetch(token ? `${url}?token=${encodeURIComponent(token)}` : url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to load preview');
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (error || !data?.content) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Failed to load spreadsheet preview
      </div>
    );
  }

  let sheets: XlsxSheet[];
  try {
    const parsed = JSON.parse(data.content);
    sheets = parsed.sheets;
  } catch {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Unable to parse spreadsheet content
      </div>
    );
  }

  if (!sheets || sheets.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No sheets found
      </div>
    );
  }

  const sheet = sheets[activeSheet];

  return (
    <div className="flex flex-col h-full">
      {/* Sheet tabs */}
      {sheets.length > 1 && (
        <div className="flex items-center gap-0 border-b border-border bg-background px-2 flex-shrink-0">
          {sheets.map((s, idx) => (
            <button
              key={idx}
              onClick={() => setActiveSheet(idx)}
              className={clsx(
                'px-3 py-1.5 text-xs font-medium border-b-2 transition-colors',
                idx === activeSheet
                  ? 'border-green-500 text-green-700 dark:text-green-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10">
            <tr>
              {sheet.columns.map((col, colIdx) => (
                <th
                  key={colIdx}
                  className="px-3 py-2 text-left text-xs font-semibold text-white bg-[#4472C4] border border-[#B4C6E7] whitespace-nowrap"
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sheet.rows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={clsx(
                  rowIdx % 2 === 0
                    ? 'bg-background'
                    : 'bg-[#D9E2F3]'
                )}
              >
                {sheet.columns.map((col, colIdx) => (
                  <td
                    key={colIdx}
                    className={clsx(
                      'px-3 py-1.5 border border-[#B4C6E7] whitespace-nowrap',
                      (col.format === 'currency' || col.format === 'number' || col.format === 'percent')
                        ? 'text-right tabular-nums'
                        : 'text-left'
                    )}
                  >
                    {formatCellValue(row[col.key], col.format)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {sheet.rows.length === 0 && (
          <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
            No data in this sheet
          </div>
        )}
      </div>

      {/* Footer with row count */}
      <div className="flex items-center px-3 py-1.5 border-t border-border bg-background flex-shrink-0">
        <span className="text-xs text-muted-foreground">
          {sheet.rows.length} row{sheet.rows.length !== 1 ? 's' : ''} · {sheet.columns.length} column{sheet.columns.length !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

// ─── DOCX / Markdown Preview ─────────────────────────────────────────

interface ContentPreviewData {
  id: string;
  fileType: string;
  title: string;
  filename: string;
  content: string;
  metadata: Record<string, unknown>;
}

const DocxPreview: React.FC<{ artifactId: string }> = ({ artifactId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['artifact-preview-docx', artifactId],
    queryFn: async (): Promise<ContentPreviewData> => {
      await ensureFreshAccessToken();
      const token = v2ChatApi.getAuthToken();
      const url = v2ChatApi.getArtifactPreviewUrl(artifactId);
      const res = await fetch(token ? `${url}?token=${encodeURIComponent(token)}` : url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to load preview');
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (error || !data?.content) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Failed to load document preview
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-background">
      <div className="docx-preview max-w-3xl mx-auto px-8 py-6 text-sm text-foreground leading-relaxed">
        <ReactMarkdown
          components={{
            h1: ({ children }) => <h1 className="text-2xl font-bold text-[#1F3864] dark:text-blue-300 mt-6 mb-3">{children}</h1>,
            h2: ({ children }) => <h2 className="text-xl font-bold text-[#1F3864] dark:text-blue-300 mt-5 mb-2">{children}</h2>,
            h3: ({ children }) => <h3 className="text-lg font-semibold text-[#1F3864] dark:text-blue-300 mt-4 mb-2">{children}</h3>,
            h4: ({ children }) => <h4 className="text-base font-semibold text-[#1F3864] dark:text-blue-300 mt-3 mb-1">{children}</h4>,
            p: ({ children }) => <p className="mb-3">{children}</p>,
            ul: ({ children }) => <ul className="list-disc pl-6 mb-3 space-y-1">{children}</ul>,
            ol: ({ children }) => <ol className="list-decimal pl-6 mb-3 space-y-1">{children}</ol>,
            li: ({ children }) => <li>{children}</li>,
            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
            em: ({ children }) => <em className="italic">{children}</em>,
            code: ({ children, className }) => {
              const isBlock = className?.includes('language-');
              return isBlock
                ? <code className="block bg-muted rounded p-3 text-xs font-mono overflow-x-auto mb-3">{children}</code>
                : <code className="bg-muted rounded px-1.5 py-0.5 text-xs font-mono">{children}</code>;
            },
            pre: ({ children }) => <pre className="mb-3">{children}</pre>,
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-blue-300 dark:border-blue-600 pl-4 my-3 text-muted-foreground italic">
                {children}
              </blockquote>
            ),
            table: ({ children }) => (
              <div className="overflow-x-auto mb-3">
                <table className="w-full border-collapse text-xs">{children}</table>
              </div>
            ),
            thead: ({ children }) => <thead>{children}</thead>,
            tbody: ({ children }) => <tbody>{children}</tbody>,
            tr: ({ children }) => <tr className="even:bg-[#D9E2F3] dark:even:bg-gray-800">{children}</tr>,
            th: ({ children }) => (
              <th className="px-3 py-2 text-left text-xs font-semibold text-white bg-[#4472C4] border border-[#B4C6E7]">
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className="px-3 py-1.5 border border-[#B4C6E7]">
                {children}
              </td>
            ),
            hr: () => <hr className="my-4 border-border" />,
            a: ({ href, children }) => (
              <a href={href} className="text-blue-600 dark:text-blue-400 underline" target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
          }}
        >
          {data.content}
        </ReactMarkdown>
      </div>
    </div>
  );
};

// ─── CSV Preview ─────────────────────────────────────────────────────

const CsvPreview: React.FC<{ artifactId: string }> = ({ artifactId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['artifact-preview-csv', artifactId],
    queryFn: async (): Promise<ContentPreviewData> => {
      await ensureFreshAccessToken();
      const token = v2ChatApi.getAuthToken();
      const url = v2ChatApi.getArtifactPreviewUrl(artifactId);
      const res = await fetch(token ? `${url}?token=${encodeURIComponent(token)}` : url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to load preview');
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (error || !data?.content) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Failed to load CSV preview
      </div>
    );
  }

  let rows: unknown[][];
  try {
    rows = JSON.parse(data.content);
  } catch {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Unable to parse CSV content
      </div>
    );
  }

  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No data found
      </div>
    );
  }

  const headers = rows[0] as unknown[];
  const dataRows = rows.slice(1);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10">
            <tr>
              {headers.map((header, idx) => (
                <th
                  key={idx}
                  className="px-3 py-2 text-left text-xs font-semibold text-white bg-[#4472C4] border border-[#B4C6E7] whitespace-nowrap"
                >
                  {String(header)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={clsx(
                  rowIdx % 2 === 0
                    ? 'bg-background'
                    : 'bg-[#D9E2F3]'
                )}
              >
                {(row as unknown[]).map((cell, colIdx) => (
                  <td
                    key={colIdx}
                    className={clsx(
                      'px-3 py-1.5 border border-[#B4C6E7] whitespace-nowrap',
                      typeof cell === 'number' ? 'text-right tabular-nums' : 'text-left'
                    )}
                  >
                    {cell === null || cell === undefined ? '' : String(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {dataRows.length === 0 && (
          <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
            No data rows
          </div>
        )}
      </div>

      <div className="flex items-center px-3 py-1.5 border-t border-border bg-background flex-shrink-0">
        <span className="text-xs text-muted-foreground">
          {dataRows.length} row{dataRows.length !== 1 ? 's' : ''} · {headers.length} column{headers.length !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

// ─── PPTX Slide Preview ──────────────────────────────────────────────

interface PptxSlide {
  title: string;
  subtitle?: string;
  content?: Array<{
    type: 'bullets' | 'table' | 'text';
    items?: string[];
    headers?: string[];
    rows?: string[][];
    value?: string;
  }>;
}

const PptxPreview: React.FC<{ artifactId: string }> = ({ artifactId }) => {
  const [activeSlide, setActiveSlide] = useState(0);

  const { data, isLoading, error } = useQuery({
    queryKey: ['artifact-preview-pptx', artifactId],
    queryFn: async (): Promise<ContentPreviewData> => {
      await ensureFreshAccessToken();
      const token = v2ChatApi.getAuthToken();
      const url = v2ChatApi.getArtifactPreviewUrl(artifactId);
      const res = await fetch(token ? `${url}?token=${encodeURIComponent(token)}` : url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to load preview');
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (error || !data?.content) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Failed to load presentation preview
      </div>
    );
  }

  let slides: PptxSlide[];
  try {
    const parsed = JSON.parse(data.content);
    slides = parsed.slides;
  } catch {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Unable to parse presentation content
      </div>
    );
  }

  if (!slides || slides.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No slides found
      </div>
    );
  }

  const slide = slides[activeSlide];

  return (
    <div className="flex flex-col h-full">
      {/* Slide card */}
      <div className="flex-1 overflow-auto p-6 flex items-start justify-center">
        <div className="w-full max-w-2xl bg-background rounded-lg shadow-lg border border-border overflow-hidden">
          {/* Title bar */}
          <div className="bg-[#1F3864] px-6 py-4">
            <h3 className="text-xl font-bold text-white">{slide.title}</h3>
            {slide.subtitle && (
              <p className="text-sm text-blue-200 mt-1">{slide.subtitle}</p>
            )}
          </div>

          {/* Content area */}
          <div className="px-6 py-5 space-y-4 min-h-[200px]">
            {slide.content?.map((block, blockIdx) => {
              switch (block.type) {
                case 'bullets':
                  return (
                    <ul key={blockIdx} className="space-y-2">
                      {block.items?.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                          <span className="text-[#4472C4] mt-1.5 text-xs">●</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  );

                case 'table':
                  return (
                    <div key={blockIdx} className="overflow-x-auto">
                      <table className="w-full border-collapse text-xs">
                        <thead>
                          <tr>
                            {block.headers?.map((h, i) => (
                              <th key={i} className="px-3 py-2 text-left font-semibold text-white bg-[#4472C4] border border-[#B4C6E7]">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {block.rows?.map((row, rowIdx) => (
                            <tr key={rowIdx} className={rowIdx % 2 === 1 ? 'bg-[#D9E2F3]' : ''}>
                              {row.map((cell, cellIdx) => (
                                <td key={cellIdx} className="px-3 py-1.5 border border-[#B4C6E7] text-foreground">
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );

                case 'text':
                  return (
                    <p key={blockIdx} className="text-sm text-foreground leading-relaxed">
                      {block.value}
                    </p>
                  );

                default:
                  return null;
              }
            })}

            {(!slide.content || slide.content.length === 0) && (
              <p className="text-sm text-muted-foreground italic">No content on this slide</p>
            )}
          </div>
        </div>
      </div>

      {/* Slide navigation footer */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-border bg-background flex-shrink-0">
        <button
          onClick={() => setActiveSlide(Math.max(0, activeSlide - 1))}
          disabled={activeSlide === 0}
          className={clsx(
            'p-1 rounded transition-colors',
            activeSlide > 0
              ? 'text-muted-foreground hover:text-foreground hover:bg-muted'
              : 'text-muted-foreground/60 cursor-not-allowed'
          )}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <span className="text-xs text-muted-foreground tabular-nums">
          Slide {activeSlide + 1} of {slides.length}
        </span>

        <button
          onClick={() => setActiveSlide(Math.min(slides.length - 1, activeSlide + 1))}
          disabled={activeSlide === slides.length - 1}
          className={clsx(
            'p-1 rounded transition-colors',
            activeSlide < slides.length - 1
              ? 'text-muted-foreground hover:text-foreground hover:bg-muted'
              : 'text-muted-foreground/60 cursor-not-allowed'
          )}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ─── Main Panel ──────────────────────────────────────────────────────

const ArtifactPreviewPanel: React.FC<ArtifactPreviewPanelProps> = ({
  artifact,
  isOpen,
  onClose,
}) => {
  const [activeArtifactId, setActiveArtifactId] = useState(artifact.artifactId);
  const [activeVersion, setActiveVersion] = useState(artifact.version);
  const [activeFileType, setActiveFileType] = useState(artifact.fileType);
  const [isDownloading, setIsDownloading] = useState(false);

  // Sync when a new artifact is opened from outside
  React.useEffect(() => {
    setActiveArtifactId(artifact.artifactId);
    setActiveVersion(artifact.version);
    setActiveFileType(artifact.fileType);
  }, [artifact.artifactId, artifact.version, artifact.fileType]);

  // Fetch version list for navigation
  const { data: versions } = useQuery({
    queryKey: ['artifact-versions', artifact.artifactGroupId],
    queryFn: () => v2ChatApi.getArtifactVersions(artifact.artifactGroupId),
    enabled: isOpen,
  });

  const canGoPrev = activeVersion > 1;
  const canGoNext = versions ? activeVersion < versions.length : false;

  const navigateVersion = (direction: 'prev' | 'next') => {
    if (!versions) return;
    const targetVersion = direction === 'prev' ? activeVersion - 1 : activeVersion + 1;
    const target = versions.find(v => v.version === targetVersion);
    if (target) {
      setActiveArtifactId(target.id);
      setActiveVersion(target.version);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await v2ChatApi.downloadArtifact(activeArtifactId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = artifact.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Download errors are visible to the user via the browser
    } finally {
      setIsDownloading(false);
    }
  };

  const { icon: FileIcon, bgClass, iconClass } = getFileTypeIcon(activeFileType);

  // Build preview URL with auth token (for iframe-based previews)
  const previewUrl = (() => {
    const base = v2ChatApi.getArtifactPreviewUrl(activeArtifactId);
    const token = v2ChatApi.getAuthToken();
    return token ? `${base}?token=${encodeURIComponent(token)}` : base;
  })();

  if (!isOpen) return null;

  return (
    <div className="flex flex-col h-full border-l border-border bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-14 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className={clsx('flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center', bgClass)}>
            <FileIcon className={clsx('w-4 h-4', iconClass)} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-foreground truncate">
              {artifact.title}
            </h2>
            <p className="text-xs text-muted-foreground">
              {artifact.fileType.toUpperCase()} · {formatFileSize(artifact.fileSizeBytes)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Version navigation */}
          {versions && versions.length > 1 && (
            <div className="flex items-center gap-0.5 mr-2">
              <button
                onClick={() => navigateVersion('prev')}
                disabled={!canGoPrev}
                className={clsx(
                  'p-1 rounded transition-colors',
                  canGoPrev
                    ? 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    : 'text-muted-foreground/60 cursor-not-allowed'
                )}
                aria-label="Previous version"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs font-medium text-muted-foreground px-1 tabular-nums">
                v{activeVersion}
              </span>
              <button
                onClick={() => navigateVersion('next')}
                disabled={!canGoNext}
                className={clsx(
                  'p-1 rounded transition-colors',
                  canGoNext
                    ? 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    : 'text-muted-foreground/60 cursor-not-allowed'
                )}
                aria-label="Next version"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Download */}
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Download artifact"
          >
            {isDownloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
          </button>

          {/* Close */}
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close preview"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Preview content */}
      <div className="flex-1 bg-muted overflow-hidden">
        {activeFileType === 'xlsx' ? (
          <XlsxPreview artifactId={activeArtifactId} />
        ) : activeFileType === 'csv' ? (
          <CsvPreview artifactId={activeArtifactId} />
        ) : (activeFileType === 'docx' || activeFileType === 'md') ? (
          <DocxPreview artifactId={activeArtifactId} />
        ) : activeFileType === 'pptx' ? (
          <PptxPreview artifactId={activeArtifactId} />
        ) : IFRAME_TYPES.has(activeFileType) ? (
          <iframe
            key={activeArtifactId}
            src={previewUrl}
            className="w-full h-full border-0"
            title={`Preview: ${artifact.title}`}
          />
        ) : (
          // Fallback for unsupported preview types
          <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
            <FileIcon className="w-12 h-12" />
            <p className="text-sm">Preview not available for {activeFileType.toUpperCase()} files</p>
            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              Download to view
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtifactPreviewPanel;
